/**
 * @fileoverview Component for displaying staff members in a table format.
 * This component includes a search bar, filters, and pagination.
 */

import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { DateTime } from 'luxon'
import { useParams } from 'react-router-dom'
import PersonIcon from '../../../assets/person.svg'
import { useGetStaffCountForSiteIdQuery, useGetStaffForSiteIdQuery } from '../../../services/staff'
import { formatOutput } from '../../../utils/formatOutput'
import { Search } from '../../elements/Search'
import { Pagination } from '../../Pagination'

/**
 * @function isAdmin
 * @description Checks if a staff member is an administrator.
 * @param {Novoic.API.Staff} staff - The staff member to check.
 * @returns {boolean} - True if the staff member is an administrator, false otherwise.
 */
function isAdmin(staff: Novoic.API.Staff): boolean {
    return staff.roles[0].role.name === 'Administrator'
}

/**
 * @function StaffTab
 * @description Component for displaying staff members in a table format.
 * @returns {JSX.Element} - A React component that renders the staff members.
 */
export function StaffTab() {
    const params = useParams()
    const { data: staff, isLoading } = useGetStaffForSiteIdQuery(params.siteId)
    const { data: total } = useGetStaffCountForSiteIdQuery(params.siteId)

    const columns = ['Staff', 'Study', 'Role', 'Invited', 'Last active']

    function formatDate(timestamp: string): JSX.Element {
        const dt = DateTime.fromISO(timestamp)
        return <span>{dt.toRelative()}</span>
    }

    if (isLoading) {
        return null
    }

    return (
        <>
            <div className="flex sticky top-[177px] bg-white border-b px-8 py-6">
                <Search placeholder="Search by staff name or email..." />
            </div>
            <table className="w-full">
                <thead className="nv-thead top-[266px] sticky">
                    <tr className="h-12">
                        {columns.map((col, index) => (
                            <th
                                key={col}
                                scope="col"
                                className={`${
                                    index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                                } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                            >
                                <button className="group inline-flex">
                                    {col}
                                    <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {staff.map((s: Novoic.API.Staff) => (
                        <tr key={s.id} className="hover:bg-[#f7f9ff] cursor-pointer transition-all">
                            <td
                                className="pl-8 flex items-center gap-x-2 whitespace-nowrap pr-10 py-4 text-sm"
                                role="presentation"
                            >
                                <div className="flex gap-x-3">
                                    <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full">
                                        <PersonIcon className="fill-indigo-500 stroke-indigo-50" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{formatOutput(s.name)}</p>
                                        <p className="text-gray-600 text-xs font-normal">
                                            {formatOutput(s.email)}
                                        </p>
                                    </div>
                                </div>
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm">
                                {formatOutput(isAdmin(s) ? 'All studies' : s.roles[0]?.study?.name)}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm">
                                {formatOutput(s.roles?.[0]?.role.name)}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                                {formatDate(s.created_at)}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                                {formatDate(s.last_activity_at)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                <Pagination total={total?.count} />
            </div>
        </>
    )
}
